import React, { useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  Alert,
  Dialog,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  CloudUpload as UploadIcon,
  Visibility as ViewIcon
} from "@mui/icons-material";

const FileAttachmentManager = ({ attachments, onAttachmentAdd, onAttachmentRemove, disabled = false }) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewDialog, setPreviewDialog] = useState({ open: false, attachment: null });

  // File limits
  const MAX_FILES = 10;
  const MAX_FILE_SIZE = 16 * 1024 * 1024; // 16MB (WhatsApp limit)
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB for images
  
  // Supported file types
  const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const SUPPORTED_FILE_TYPES = [
    ...SUPPORTED_IMAGE_TYPES,
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv'
  ];

  // Handle file selection
  const handleFileSelect = (files) => {
    const remaining = MAX_FILES - attachments.length;
    if (remaining <= 0) {
      alert(`Maximum ${MAX_FILES} files allowed.`);
      return;
    }

    const validFiles = [];
    const errors = [];

    Array.from(files).forEach(file => {
      // Check file type
      if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Unsupported file type`);
        return;
      }

      // Check file size
      const maxSize = SUPPORTED_IMAGE_TYPES.includes(file.type) ? MAX_IMAGE_SIZE : MAX_FILE_SIZE;
      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        errors.push(`${file.name}: File too large (max ${maxSizeMB}MB)`);
        return;
      }

      validFiles.push(file);
    });

    // Enforce max file count
    const filesToAdd = validFiles.slice(0, remaining);
    if (validFiles.length > remaining) {
      errors.push(`Only ${remaining} more file(s) can be added (max ${MAX_FILES} total).`);
    }

    if (errors.length > 0) {
      alert('Some files could not be added:\n' + errors.join('\n'));
    }

    if (filesToAdd.length > 0) {
      onAttachmentAdd(filesToAdd);
    }
  };

  // Handle file input change
  const handleFileInputChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  // Handle drag and drop
  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    
    if (disabled) return;
    
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  // Open file dialog
  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file type icon
  const getFileTypeIcon = (type) => {
    if (SUPPORTED_IMAGE_TYPES.includes(type)) {
      return <ImageIcon color="secondary" />;
    }
    return <FileIcon color="action" />;
  };

  // Handle preview
  const handlePreview = (attachment) => {
    if (attachment.type === 'image') {
      setPreviewDialog({ open: true, attachment });
    }
  };

  // Close preview
  const closePreview = () => {
    setPreviewDialog({ open: false, attachment: null });
  };

  const isAtLimit = attachments.length >= MAX_FILES;
  const isUploadDisabled = disabled || isAtLimit;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AttachFileIcon />
        Attachments ({attachments.length}/{MAX_FILES})
      </Typography>

      {/* File Upload Area */}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          border: 2,
          borderStyle: 'dashed',
          borderColor: dragOver ? 'secondary.main' : isAtLimit ? 'warning.main' : 'divider',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          bgcolor: dragOver ? 'action.hover' : 'background.default',
          cursor: isUploadDisabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          mb: 2,
          opacity: isAtLimit ? 0.6 : 1,
          '&:hover': {
            borderColor: isUploadDisabled ? (isAtLimit ? 'warning.main' : 'divider') : 'secondary.light',
            bgcolor: isUploadDisabled ? 'background.default' : 'action.hover'
          }
        }}
        onClick={openFileDialog}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          accept={SUPPORTED_FILE_TYPES.join(',')}
          multiple
          style={{ display: 'none' }}
          disabled={isUploadDisabled}
        />

        <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        {isAtLimit ? (
          <Typography variant="body1" color="warning.main" gutterBottom>
            Maximum {MAX_FILES} files reached
          </Typography>
        ) : (
          <Typography variant="body1" color="textPrimary" gutterBottom>
            Drop files here or click to select
          </Typography>
        )}
        <Typography variant="body2" color="textSecondary">
          Supported: Images (JPG, PNG, GIF), Documents (PDF, DOC, XLS), Text files
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Max size: 5MB for images, 16MB for documents · Max {MAX_FILES} files per send
        </Typography>
      </Box>

      {/* Attachments List */}
      {attachments.length > 0 && (
        <Card variant="outlined">
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <List dense>
              {attachments.map((attachment, index) => (
                <ListItem
                  key={attachment.id}
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    mb: index < attachments.length - 1 ? 1 : 0,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <ListItemIcon>
                    {attachment.preview ? (
                      <Avatar
                        src={attachment.preview}
                        sx={{ width: 40, height: 40 }}
                      />
                    ) : (
                      getFileTypeIcon(attachment.file.type)
                    )}
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={attachment.name}
                    secondary={
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {formatFileSize(attachment.size)}
                        </Typography>
                        {attachment.type === 'image' && (
                          <Chip
                            label="Image"
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {attachment.type === 'image' && (
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(attachment)}
                          disabled={disabled}
                        >
                          <ViewIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => onAttachmentRemove(attachment.id)}
                        disabled={disabled}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* File Size Warning */}
      {attachments.length > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2">
            Total attachments: {attachments.length} files
            {attachments.length > 5 && (
              <span style={{ color: 'orange' }}> (Consider reducing the number of attachments for better delivery)</span>
            )}
          </Typography>
        </Alert>
      )}

      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={closePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {previewDialog.attachment && (
            <img
              src={previewDialog.attachment.preview}
              alt={previewDialog.attachment.name}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview} color="secondary">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileAttachmentManager;